/**
 * SalesAccountDetail — the Slice A "360 view": GET /v1/sales/accounts/{id}
 * (A1) in ONE read — the account header, its contacts, its deals split
 * open/closed, the activities logged against those deals or contacts (newest
 * first, the server caps at 100) and the issued invoices behind its won deals.
 *
 * Header edits are inline (P1-1 accepted: name / website / phone / notes) and
 * go through PATCH /v1/sales/accounts/{id} — every header field is sent, so a
 * cleared input clears the column ("" = clear). The mutation response
 * overrides the fetched account the SalesDealDetail way (keyed override,
 * never copied into state from the fetch itself).
 *
 * Money: the header's open-pipeline figure applies the SAME fenced rule the
 * list's server aggregate does (GBP rows as themselves, captured non-GBP rows
 * by their base, uncaptured rows counted out and SAID) — never a
 * mixed-currency sum. Each deal row shows its own value in its own currency.
 *
 * Visual (docs/design/stier/ADOPTION.md): the Deal Detail page grammar —
 * back link, header, two-column .k-card body (§3.4 contract; §8.4 slice-2
 * treatment). Stage chips are DealStageBadge (§8.4: .stage-badge stays the
 * capsule it already was), invoice status is .k-badge-outline (§3.4),
 * inputs keep the control radius (§8.4 REFUSED list), buttons are the k-btn
 * family. The timeline reuses Deal Detail's row markup. No new tokens.
 */

import { useState } from 'react'
import { ArrowLeft, Globe, Phone, Mail, Users, FileText, Pencil } from 'lucide-react'
import { Avatar, DealStageBadge, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesAccountView, SalesDeal } from '../../types'
import { useAsync, fetchAccount, updateAccount, relAgo, fmtDate } from '../../api'
import { SalesAuditStrip } from './SalesAuditStrip'
import { money } from '../../../../lib/money'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { accent } from '../../../../theme/tokens'

const TYPE_ICON: Record<string, React.ReactNode> = {
  call: <Phone size={13} />,
  email: <Mail size={13} />,
  meeting: <Users size={13} />,
  note: <FileText size={13} />,
}

/** A website stored without a scheme still needs one to be a link. */
function hrefFor(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`
}

function DealRow({ deal, onOpen }: { deal: SalesDeal; onOpen: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onOpen(deal.id)}
      className="w-full text-left flex items-center justify-between gap-3 py-3 border-b border-edge-row last:border-0 hover:bg-surface-row-hover transition-colors">
      <div className="min-w-0">
        <div className="text-13 font-semibold text-ink-primary truncate">{deal.name}</div>
        <div className="text-11 text-ink-faint mt-0.5">
          {deal.owner !== '—' ? `Owned by ${deal.owner}` : 'Unassigned'}
          {deal.closedDate ? ` · closed ${deal.closedDate}` : ` · ${deal.lastActivity}`}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-13 font-medium text-ink-primary">{money(deal.amount / 100, deal.currency)}</span>
        <DealStageBadge stage={deal.stage} />
      </div>
    </button>
  )
}

interface HeaderDraft { name: string; website: string; phone: string; notes: string }

export function SalesAccountDetail({ accountId, onBack, onSelectDeal, onNavigate }: {
  accountId: string
  onBack: () => void
  onSelectDeal: (dealId: string) => void
  onNavigate?: (dest: string) => void
}) {
  const q = useAsync(() => fetchAccount(accountId), [accountId])
  const [override, setOverride] = useState<{ id: string; account: SalesAccountView } | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<HeaderDraft>({ name: '', website: '', phone: '', notes: '' })
  const [saving, setSaving] = useState(false)

  if (q.loading) return <LoadingState />
  if (q.error || !q.data) return <ErrorState message={q.error ?? 'Account not found'} />

  const view = q.data
  const account = override && override.id === accountId ? override.account : view.account

  const openValuePence = view.openDeals.reduce((s, d) => s + (d.basePence ?? 0), 0)
  const openExcluded = view.openDeals.filter(d => d.basePence === null).length

  function startEdit() {
    setDraft({ name: account.name, website: account.website, phone: account.phone, notes: account.notes })
    setEditing(true)
  }

  async function save() {
    if (!draft.name.trim()) { toast.error('Account name is required'); return }
    setSaving(true)
    try {
      const next = await updateAccount(accountId, {
        name: draft.name.trim(),
        website: draft.website.trim(),
        phone: draft.phone.trim(),
        notes: draft.notes.trim(),
      })
      setOverride({ id: accountId, account: next })
      setEditing(false)
      toast.success('Account updated')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update account'))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none'

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Back link */}
      <button onClick={onBack}
        className="flex items-center gap-2 text-12 font-medium text-ink-secondary hover:text-ink-primary transition-colors self-start">
        <ArrowLeft size={14} /> Back to accounts
      </button>

      {/* Header — read view, or the inline edit form in its place */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {editing ? (
          <div className="flex-1 min-w-0 max-w-2xl flex flex-col gap-3">
            <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              placeholder="Account name" aria-label="Account name" autoFocus
              className={`${inputCls} text-14 font-semibold`} />
            <div className="grid grid-cols-2 gap-3">
              <input value={draft.website} onChange={e => setDraft(d => ({ ...d, website: e.target.value }))}
                placeholder="Website" aria-label="Website" className={inputCls} />
              <input value={draft.phone} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
                placeholder="Phone" aria-label="Phone" className={inputCls} />
            </div>
            <textarea value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
              rows={3} placeholder="Notes" aria-label="Notes" className={`${inputCls} h-auto py-2 resize-none`} />
            <div className="flex items-center gap-2">
              <button className="k-btn disabled:opacity-50" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="k-btn-secondary" disabled={saving} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <Avatar name={account.name} size={40} />
              <div className="min-w-0">
                <h1 className="text-section-title font-semibold text-ink-primary leading-tight truncate">{account.name}</h1>
                <p className="text-13 text-ink-secondary mt-1 flex items-center gap-4 flex-wrap">
                  {account.website ? (
                    <a href={hrefFor(account.website)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:underline" style={{ color: accent.DEFAULT }}>
                      <Globe size={12} />{account.website}
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-ink-faint"><Globe size={12} />No website</span>
                  )}
                  {account.phone ? (
                    <span className="inline-flex items-center gap-1"><Phone size={12} className="text-ink-faint" />{account.phone}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-ink-faint"><Phone size={12} />No phone</span>
                  )}
                </p>
              </div>
            </div>
            {account.notes && (
              <p className="text-12 text-ink-secondary mt-3 max-w-2xl whitespace-pre-wrap">{account.notes}</p>
            )}
          </div>
        )}

        {!editing && (
          <div className="text-right">
            <div className="text-overline text-ink-faint uppercase tracking-wider mb-1">Open pipeline</div>
            <div className="text-section-title font-bold text-ink-primary">
              {view.openDeals.length > 0 ? money(openValuePence / 100, 'GBP') : '—'}
            </div>
            <div className="text-11 text-ink-faint mt-0.5">
              {view.openDeals.length} open · {view.closedDeals.length} closed · {view.contacts.length} contact{view.contacts.length === 1 ? '' : 's'}
              {openExcluded > 0 && ` · ${openExcluded} unconverted excluded`}
            </div>
            <div className="flex items-center gap-2 mt-2 justify-end">
              <button className="k-btn-secondary inline-flex items-center gap-1" onClick={startEdit}>
                <Pencil size={12} /> Edit
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Two-column body: Deals / Timeline | Contacts / Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Deals — open first, closed beneath; every row opens Deal Detail */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Deals</div>
                <div className="k-card-sub">{view.openDeals.length} open · {view.closedDeals.length} closed</div>
              </div>
            </div>
            <div className="px-5 py-2">
              <div className="k-eyebrow uppercase pt-2 pb-1">Open deals</div>
              {view.openDeals.length === 0 && <div className="text-13 text-ink-muted py-3">No open deals.</div>}
              {view.openDeals.map(d => <DealRow key={d.id} deal={d} onOpen={onSelectDeal} />)}
              <div className="k-eyebrow uppercase pt-4 pb-1">Closed deals</div>
              {view.closedDeals.length === 0 && <div className="text-13 text-ink-muted py-3">No closed deals.</div>}
              {view.closedDeals.map(d => <DealRow key={d.id} deal={d} onOpen={onSelectDeal} />)}
            </div>
          </div>

          {/* Activity timeline — the Deal Detail row markup */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Activity timeline</div>
                <div className="k-card-sub">Logged against this account's deals and contacts</div>
              </div>
            </div>
            <div className="p-5 flex flex-col gap-4">
              {view.activities.length === 0 && <div className="text-13 text-ink-muted">No activity logged yet.</div>}
              {view.activities.map(a => (
                <div key={a.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-faint flex-shrink-0">
                    {TYPE_ICON[a.activityType] ?? <FileText size={13} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-13 font-semibold text-ink-primary">{a.subject}</div>
                    {a.body && <div className="text-12 text-ink-secondary mt-1">{a.body}</div>}
                    <div className="text-11 text-ink-faint mt-1">{relAgo(a.createdAt)} · {fmtDate(a.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slice B (0117): the audit strip. Accounts have no stage, so this
              mount reads field history only. */}
          <SalesAuditStrip recordType="accounts" recordId={accountId} />
        </div>

        <div className="flex flex-col gap-4">
          {/* Contacts */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Contacts</div>
            </div>
            <div className="px-5 py-2">
              {view.contacts.length === 0 && <div className="text-13 text-ink-muted py-3">No contacts linked.</div>}
              {view.contacts.map(c => (
                <div key={c.id} className="flex items-center gap-3 py-3 border-b border-edge-row last:border-0">
                  <Avatar name={c.name} size={32} />
                  <div className="min-w-0">
                    <div className="text-13 font-semibold text-ink-primary truncate">{c.name}</div>
                    <div className="text-12 text-ink-muted truncate">{[c.title, c.email].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invoices — the issued invoices behind won deals (C5-3: number +
              tab navigation; no per-invoice deep link exists) */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Invoices</div>
            </div>
            <div className="px-5 py-2">
              {view.invoices.length === 0 && <div className="text-13 text-ink-muted py-3">No invoices issued.</div>}
              {view.invoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between gap-3 py-3 border-b border-edge-row last:border-0">
                  <div className="min-w-0">
                    {onNavigate ? (
                      <button type="button" onClick={() => onNavigate('finance.issued-invoices')} title="View in Issued Invoices"
                        className="text-13 font-medium hover:underline" style={{ color: accent.DEFAULT }}>
                        {inv.invoiceNumber}
                      </button>
                    ) : (
                      <div className="text-13 font-medium text-ink-primary">{inv.invoiceNumber}</div>
                    )}
                    <div className="text-11 text-ink-faint mt-0.5">{inv.issueDate ? fmtDate(inv.issueDate) : '—'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-13 font-medium text-ink-primary">{money(inv.total, inv.currency)}</span>
                    <span className="k-badge-outline text-ink-muted">{inv.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
